import { ReactNode } from 'react'
import dayjs from 'dayjs'
import {
  TIME_GRANULARITY,
  TDateRange,
} from '../../../types/models/DataAnalysis'
import { Entity } from '../../../types/permissions'
import { SettingsType } from '../../../utils/hooks/useSystemSettings'

export type TTabs = {
  id: number
  label: string
  permission?: Entity
  settings?: SettingsType
}

export const tabsData: TTabs[] = [
  {
    id: 1,
    label: 'shipments',
    permission: Entity.WHEAT_CODE,
    settings: 'SHOW_WHEAT',
  },
  {
    id: 2,
    label: 'storage',
    permission: Entity.WHEAT_CODE,
    settings: 'SHOW_WHEAT',
  },
  {
    id: 3,
    label: 'blends',
    permission: Entity.WHEAT_CODE,
    settings: 'SHOW_WHEAT',
  },
  // {
  //   id: 4,
  //   label: 'flours',
  //   permission: Entity.FLOURS,
  //   settings: 'SHOW_FLOURS',
  // }, // TODO: enable when ready
]

export enum DATA_ANALYSIS_FILTERS {
  DATE = 'date',
  WHEAT_CODE = 'wheatCode',
  BIN = 'bin',
  BLENDS = 'blends',
  PRODUCTS = 'products',
  FLOUR_PRODUCT = 'flour_product',
}

export enum TAB_KEYS {
  PARAMETERS_TREND = 'parameters_trend',
  PARAMETERS_COMPARISON = 'parameters_comparison',
  CONSISTENCY_TREND = 'consistency_trend',
  BIN_DISTRIBUTION = 'bin_distribution',
  SPECTRAL_IMAGE_COMPARISON = 'spectral_image_comparison',
  COST_TRENDS = 'cost_trends',
  COMPOSITIONS = 'compositions',
  STORAGE_DISTRIBUTION = 'storage_distribution',
}

export enum DATA_ANALYSIS_INFO {
  BIN = 'bin',
  CONSISTENCY = 'consistency',
  PRODUCT = 'product',
  WHEAT_CODE = 'wheatCode',
}

export type TTabConfig = {
  id: number
  label: string
  key: string
  filters?: DATA_ANALYSIS_FILTERS[]
  info?: DATA_ANALYSIS_INFO[]
  content?: () => ReactNode
}

export const shipmentNestedTabs: TTabConfig[] = [
  {
    id: 1,
    key: 'parameters_comparison',
    label: 'Parameters Comparison',
    filters: [DATA_ANALYSIS_FILTERS.DATE, DATA_ANALYSIS_FILTERS.WHEAT_CODE],
  },
  {
    id: 2,
    key: 'parameters_trend',
    label: 'Parameters Trend',
    filters: [DATA_ANALYSIS_FILTERS.DATE, DATA_ANALYSIS_FILTERS.WHEAT_CODE],
  },
  {
    id: 3,
    key: 'consistency_trend',
    label: 'Consistency Trend',
    filters: [DATA_ANALYSIS_FILTERS.DATE, DATA_ANALYSIS_FILTERS.WHEAT_CODE],
    info: [DATA_ANALYSIS_INFO.CONSISTENCY],
  },
  // Hiding this chart temporarily until implemented in backend
  // {
  //   id: 4,
  //   key: 'spectral_image_comparison',
  //   label: 'Spectral Image Comparison',
  //   filters: [DATA_ANALYSIS_FILTERS.DATE, DATA_ANALYSIS_FILTERS.WHEAT_CODE],
  // },
  {
    id: 5,
    key: 'bin_distribution',
    label: 'Bin Distribution',
    filters: [DATA_ANALYSIS_FILTERS.DATE, DATA_ANALYSIS_FILTERS.WHEAT_CODE],
    info: [DATA_ANALYSIS_INFO.BIN],
  },
]

export const storageNestedTabs: TTabConfig[] = [
  {
    id: 1,
    key: 'storage_distribution',
    label: 'Storage Distribution',
    filters: [DATA_ANALYSIS_FILTERS.DATE, DATA_ANALYSIS_FILTERS.WHEAT_CODE],
  },
]

export const blendsNestedTabs: TTabConfig[] = [
  {
    id: 1,
    key: 'compositions',
    label: 'Compositions',
    filters: [DATA_ANALYSIS_FILTERS.DATE, DATA_ANALYSIS_FILTERS.FLOUR_PRODUCT],
    info: [DATA_ANALYSIS_INFO.WHEAT_CODE],
  },
  {
    id: 2,
    key: 'cost_trends',
    label: 'Cost Trends',
    filters: [DATA_ANALYSIS_FILTERS.DATE, DATA_ANALYSIS_FILTERS.PRODUCTS],
  },
]

export type TInfoValues = {
  id: number
  name: string
  color: string
}

export const consistencyInfoList: TInfoValues[] = [
  { id: 1, name: 'Consistent', color: '#36C24C' },
  { id: 2, name: 'Borderline', color: '#FFD747' },
  { id: 3, name: 'Inconsistent', color: '#FF0009' },
]

// Small helper for ui presentation
export const prettyAttr = (k: string) =>
  k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

export const getTabByKey = (
  key: string,
  section: 'shipment' | 'storage' | 'blends' = 'shipment'
) => {
  switch (section) {
    case 'shipment':
      return shipmentNestedTabs.find((tab) => tab.key === key)
    case 'storage':
      return storageNestedTabs.find((tab) => tab.key === key)
    case 'blends':
      return blendsNestedTabs.find((tab) => tab.key === key)
  }
}

export const granularityOptions = [
  { value: TIME_GRANULARITY.DAILY, label: 'Daily' },
  { value: TIME_GRANULARITY.MONTHLY, label: 'Monthly' },
  { value: TIME_GRANULARITY.YEARLY, label: 'Yearly' },
]

// Default date range used across Data Analysis (last month -> yesterday)
export const getDefaultDateRange = (
  reference: dayjs.Dayjs = dayjs()
): TDateRange => {
  const endDate = reference.subtract(1, 'day')
  const startDate = endDate.subtract(1, 'month')
  return {
    from: startDate.format('YYYY-MM-DD'),
    to: endDate.format('YYYY-MM-DD'),
  }
}
